import { fromColorPathToColor, WeenatColor } from '@weenat/theme'
import { isNil } from 'lodash-es'
import { memo, ReactNode } from 'react'
import { toast as sonnerToast } from 'sonner'
import { styled } from 'styled-components'
import Button from './Button'
import Card from './Card'
import Icons from './Icons'
import Text from './Text'

type ToastKind = 'error' | 'success' | 'warning' | 'info'

const ToastContainer = styled(Card)`
  position: relative;

  flex: 1;

  margin: ${(p) => p.theme.spacings.md}px;
  padding: 0px;

  width: 100%;
  max-width: 400px;
  min-width: 256px;

  font-size: ${(p) => p.theme.typography.sizes.sm}px;
  font-weight: ${(p) => p.theme.typography.weights.regular};
  line-height: ${(p) => p.theme.typography.lineHeights.rg}px;

  background-color: ${(p) => p.theme.colors.grayscale[50]};
  border-color: ${(p) => p.$borderColor};
  border-radius: ${(p) => p.theme.radiuses.md}px;

  box-shadow: ${(p) => p.theme.shadows.md.boxShadow};

  overflow: unset;

  p,
  b {
    user-select: none;
  }
`

const AbsoluteCloseButton = styled.div`
  position: absolute;

  display: flex;
  align-items: center;
  justify-content: center;

  top: -10px;
  right: -10px;

  box-shadow: ${(p) => p.theme.shadows.sm.boxShadow};
  border-radius: ${(p) => p.theme.radiuses.rounded}px;

  cursor: pointer;
`

const actionButtonStyles = {
  borderRadius: 4
}

interface ToastProps {
  id: string | number
  title: ReactNode
  description?: ReactNode
  action?: {
    label: string
    onClick: () => void
  }
  kind: ToastKind
}

const Toast: FC<ToastProps> = ({ id, title, description, action, kind }) => {
  let color: WeenatColor | undefined = undefined
  let Icon = undefined

  switch (kind) {
    case 'success':
      color = 'feedback.success.500'
      Icon = Icons.CheckCircle
      break
    case 'error':
      color = 'feedback.error.500'
      Icon = Icons.AlertTriangle
      break
    case 'warning':
      color = 'feedback.warning.500'
      Icon = Icons.AlertTriangle
      break
    default:
      color = 'primary.500'
      Icon = Icons.InfoCircle
      break
  }

  const borderColor = fromColorPathToColor(color)

  return (
    <ToastContainer key={id} $borderColor={borderColor}>
      <Flex
        $alignItems='center'
        $justifyContent='space-between'
        $py={12}
        $pl={8}
        $pr={12}
        $gap='md'
      >
        <Icon $size='lg' $color={color} />
        <Flex $flex={1}>
          <Box width='100%'>
            {typeof title === 'string' ? (
              <Text $fontSize='sm' $fontWeight='bold' $color='grayscale.black'>
                {title}
              </Text>
            ) : (
              title
            )}
            {!isNil(description) ? (
              <>
                {typeof description === 'string' ? (
                  <Text $fontSize='sm' $color='grayscale.black' $lineHeight='sm'>
                    {description}
                  </Text>
                ) : (
                  description
                )}
              </>
            ) : null}
          </Box>
        </Flex>
        {!isNil(action) ? (
          <Button
            variant='small'
            color='grayscale.white'
            backgroundColor='grayscale.black'
            onPress={() => {
              action.onClick()
              sonnerToast.dismiss(id)
            }}
            style={actionButtonStyles}
          >
            {action.label}
          </Button>
        ) : null}
      </Flex>

      <AbsoluteCloseButton>
        <Icons.Close
          $size='md'
          $rounded
          onPress={() => sonnerToast.dismiss(id)}
          $backgroundColor='grayscale.50'
          $borderColor={borderColor}
        />
      </AbsoluteCloseButton>
    </ToastContainer>
  )
}

export default memo(Toast)
